<template>
  <v-card
    v-if="!isMobile()"
    style="border: 1px solid var(--tf-border-strong); padding: 14px"
    data-testid="ProductCard"
    class="elevation-2 pcard pcard-clickable"
    role="link"
    tabindex="0"
    :aria-label="`Open ${campaignProduct.product_name || 'product'} in the editor`"
    @mouseover="mouseOverImage = true"
    @mouseleave="mouseOverImage = false"
    @click="editProduct"
    @keydown.enter="editProduct"
    @keydown.space.prevent="editProduct"
  >
    <!--
      Store-editor Products page polish (Jake direct ask, no card --
      polish/UX carve-out): the whole card opens the product editor
      (editProduct(), the SAME destination the Edit button already goes
      to). Every EXISTING interactive control (readiness flag, share/QR,
      Edit, Hide/Show, Delete) plus the two NEW interactive surfaces added
      alongside this (carousel arrows, color swatches) stop propagation
      via @click.stop so they do not ALSO trigger the card-level navigate.
      The parent (StoreProducts/index.vue) wraps this card with its own
      selection checkbox and already @click.stop's that overlay, so it is
      unaffected by this change.
    -->
    <!--
      Day 6.5 /gate round-1 P2: the "Loading Smart Product Data" dialog
      was orphaned. It was only ever set to true inside the
      now-deleted smart-product hydration in the legacy editProduct()
      modal path. With that path gone (pencil now navigates to the
      canonical product editor), the dialog could never open. Removed
      with the loadingSmartProduct data field. The legacy modal itself
      was deleted in the 2026-09-01 dead-code sweep.
    -->
    <!--Top Indicators-->
    <v-card elevation="0" width="100%" height="40">
      <div class="d-flex align-center justify-start pcard-top-row">
        <!--
          #2259: the parent grid (StoreProducts/index.vue) overlays an
          absolutely-positioned selection checkbox at the card's top-left
          corner (top:8px;left:8px, 28px box). The left cluster is offset
          right (pcard-top-left-cluster padding-left) so the Smart-Product
          bolt clears the checkbox instead of sitting under it, and the
          readiness flag was moved to the right-hand cluster (after the
          v-spacer) so nothing else competes for the top-left corner.
        -->
        <div class="d-flex align-center pcard-top-left-cluster">
          <v-tooltip location="right" v-if="campaignProduct.is_smart_product">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon variant="plain">
                <v-icon size="large" color="amber" icon="bolt" />
              </v-btn>
            </template>
            <span> Smart Product </span>
          </v-tooltip>
        </div>
        <v-spacer />

        <!--
          Phase 2.2 readiness flag (ADR 0041). Surfaces approval state
          of the product's decorations at a glance. Click navigates to
          the Approval Workspace filtered to this product. ``readiness``
          is supplied by the parent (StoreProducts/index.vue fetches
          the per-product rollup via the storeReadiness Pinia store);
          when absent we render nothing so legacy mounts are unaffected.
          #2259: moved here (top-right) out of the top-left cluster so it
          no longer overlaps the parent's selection checkbox. The flag
          carries its own descriptive tooltip (ProductReadinessFlag
          ``tooltipText``, e.g. "N decorations pending approval") so the
          "Deco:" chip is self-explanatory on hover.
        -->
        <!--
          Store-editor Products page polish: ProductReadinessFlag emits a
          custom `click` payload object (campaignProductId/state/count),
          not the native DOM event, so a `.stop` modifier on the listener
          below only stops the CUSTOM event, not the native click that
          bubbles out of the flag's own <button>. Wrapping in a
          @click.stop div intercepts the real DOM click before it reaches
          the card root, without touching ProductReadinessFlag.vue (shared
          with ProductsTableView.vue).
        -->
        <div @click.stop>
          <product-readiness-flag
            v-if="readiness"
            :state="readiness.state"
            :count="readiness.count"
            :decoration-count="readiness.decoration_count"
            :campaign-product-id="campaignProduct.id"
            @click="onReadinessClick"
          />
        </div>

        <uncovered-colors-marker
          :count="readiness ? readiness.uncovered_color_count : 0"
          :testid="`pcard-uncovered-colors-${campaignProduct.id}`"
        />
        <invalid-placement-marker
          :invalid="Boolean(readiness && readiness.invalid_placement)"
          :testid="`pcard-invalid-placement-${campaignProduct.id}`"
        />
        <!-- Card #573: worst low-resolution state among the placements
             (stop beats warn), from the readiness rollup. -->
        <dpi-status-icon
          :severity="readiness ? readiness.dpi_status : null"
          testid="product-dpi-icon"
        />

        <v-tooltip location="left" v-if="productIncomplete">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" icon variant="plain">
              <v-icon color="error" icon="error" />
            </v-btn>
          </template>
          <span> {{ productIncompleteText }} </span>
        </v-tooltip>
      </div>
    </v-card>
    <div class="hidden-product-shade" v-if="hideFromStorefront">
      <div class="hidden-product-shade-text">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" style="font-size: 80px;" color="white" icon="visibility_off" />
          </template>
          <span>
            Product is hidden from storefront
          </span>
        </v-tooltip>
      </div>
    </div>
    <!--
      #2246 redesign: the card follows Jake's mockup top-to-bottom --
      image -> name -> #base-sku -> price -> display-only color swatches ->
      Edit / Hide / Delete. The color + size v-selects were removed (sizes
      are not surfaced on the card; the color <select> is replaced by the
      read-only swatch row below). The carousel keeps its full logic
      (possiblePrimaryValues / buildProductImages / changeSlider) so
      picker-chosen images and the color-index invariant are unchanged --
      only the on-card color CHOOSER is gone.
    -->
    <!-- Image Carousel -->
    <div class="d-flex flex-column justify-center">
      <!--
        Store-editor Products page polish (Jake direct ask, no card --
        polish/UX carve-out): the wrapper intercepts clicks on the
        Vuetify-rendered prev/next arrows (.v-window__left / .v-window__right
        -- see the root-cause note in the <style> block below) so they do
        NOT ALSO trigger the card-level navigate. We don't own the arrows'
        markup (Vuetify renders them internally), so we can't put
        @click.stop directly on them; onCarouselWrapperClick checks the
        clicked element and only stops propagation for an arrow click,
        letting an image click fall through to the card-level navigate as
        intended.
      -->
      <div
        :data-testid="showsDerivedImage ? `pe-product-card-derived-image-${campaignProduct.id}` : undefined"
        @click="onCarouselWrapperClick"
      >
        <v-carousel
          :cycle="false"
          hide-delimiters
          v-model="selectedImageId"
          @update:modelValue="changeSlider"
          style="height: 264px"
        >
          <v-carousel-item
            v-for="(item, i) in productImages"
            :key="i"
            reverse-transition="fade-transition"
            transition="fade-transition"
          >
            <v-card
              class="mb-2"
              style="height: 264px"
              elevation="0"
              color="white"
            >
              <div class="d-flex flex-column align-center justify-center" style="height: 264px">
                <img
                  v-lazy="item"
                  style="max-width: 264px; max-height: 264px"
                  class="lazy-img-fadein pt-4"
                  lazy="loaded"
                  :data-testid="`pe-product-card-img-${campaignProduct.id}-${item}`"
                />
              </div>
            </v-card>
          </v-carousel-item>
        </v-carousel>
      </div>

      <!-- Name -->
      <div class="mt-2 product-title" data-testid="pcard-name">
        {{ $props.campaignProduct.product_name }}
      </div>

      <!-- Supplier (Jake 2026-10-05). Same grey meta-line styling as the
           base SKU below it; the shared supplierLabel() rule keeps the card
           and the table view in agreement. An em dash when no supplier name
           exists -- never invented text. -->
      <div
        class="pcard-supplier"
        data-testid="pcard-supplier"
        :data-supplier-unknown="supplierName === null ? 'true' : null"
        :title="supplierName === null ? 'No supplier set' : null"
      >
        <template v-if="supplierName">{{ supplierName }}</template>
        <template v-else>&mdash;</template>
      </div>

      <!-- Base style number (NOT a variant id) -->
      <div
        v-if="baseSku"
        class="pcard-base-sku"
        data-testid="pcard-base-sku"
      >
        #{{ baseSku }}
      </div>

      <!-- Price + pricing mode (2026-09-29). The price is ONLY the server's
           quote (lowest size price from the row's `price_preview`). A row
           without a quote shows a neutral placeholder -- never the old
           default_sale_price formula, which disagreed with the charge. The
           badge says whether the price is the product's own (Fixed price)
           or the store tables' (Store pricing). -->
      <div class="pcard-price-row">
        <div
          class="price"
          data-testid="pcard-price"
          :data-price-unavailable="priceLabel === null ? 'true' : null"
          :title="priceLabel === null ? 'No price quoted yet' : null"
        >
          {{ priceLabel ?? pricePlaceholder }}
        </div>
        <pricing-mode-badge
          :mode="$props.campaignProduct.pricing_mode"
          testid="pcard-pricing-mode"
        />
        <unpriced-sizes-marker
          :sizes="unpricedSizes"
          testid="pcard-unpriced-sizes"
        />
      </div>

      <!-- Color swatches for the store's chosen colors. A filled circle in
           the color's hex + its name. Hex comes from the backend
           selected_color_hex map, then the frontend fallbackHex map, then
           a neutral grey (never blank).
           Store-editor Products page polish (Jake direct ask, no card --
           polish/UX carve-out): clicking a swatch swaps THIS card's
           carousel to that color's image -- transient only, no
           persistence (see selectSwatch()). A color with no carousel slot
           (no image) is a no-op. @click.stop keeps this from ALSO
           triggering the card-level navigate (see the v-card root). -->
      <div
        v-if="visibleSwatches.length > 0"
        class="pcard-swatches"
        data-testid="pcard-swatches"
      >
        <div
          v-for="swatch in visibleSwatches"
          :key="swatch.sku"
          class="pcard-swatch"
          :data-testid="`pcard-swatch-${swatch.sku}`"
        >
          <span
            class="pcard-swatch-dot"
            :class="{ 'is-fallback': swatch.isFallback }"
            :data-testid="`pcard-swatch-dot-${swatch.sku}`"
            :style="{ background: swatch.hex }"
            role="button"
            tabindex="0"
            :aria-label="`Preview ${swatch.sku}`"
            @click.stop="selectSwatch(swatch.sku)"
            @keydown.enter.stop="selectSwatch(swatch.sku)"
            @keydown.space.stop.prevent="selectSwatch(swatch.sku)"
          >
            <!-- #2246 declutter: the color name moved OFF the card (the
                 name-below-dot label was removed) and INTO a styled tooltip
                 on hover, replacing the laggy native ``title`` attribute.
                 ``activator="parent"`` targets the dot itself so the swatch
                 stays the hover target and keeps its data-testid. Mirrors
                 the Hide/Delete action-button tooltips above. -->
            <v-tooltip activator="parent" location="top">
              {{ swatch.sku }}
            </v-tooltip>
          </span>
        </div>
        <div
          v-if="hiddenSwatchCount > 0"
          class="pcard-swatch-more"
          data-testid="pcard-swatch-more"
          :title="`${hiddenSwatchCount} more color(s)`"
        >
          +{{ hiddenSwatchCount }} more
        </div>
      </div>
    </div>
    <v-spacer />

    <!-- Action buttons: Share (QR) / Edit / Hide from storefront / Delete.
         Each reuses the card's EXISTING handler (editProduct navigates to
         the product editor, toggleProductHideFromStorefront POSTs + emits
         hide-from-storefront-changed, deleteProduct emits delete-product).
         Share (QR) is a subtle icon-only button -- Boulders: Storefront
         color deep-link + QR share code (pebble 2 of 2),
         https://trello.com/c/PWUeRl0u -- sitting alongside the pencil/edit
         affordance, matching the card's existing icon-button styling
         (icon + variant="plain", same as the Smart-Product / readiness
         indicators in the top row) rather than a new full-width text
         button competing with Edit/Hide/Delete for space. -->
    <div class="pcard-actions" data-testid="pcard-actions">
      <v-tooltip location="top">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            variant="plain"
            class="pcard-share-btn"
            @click.stop="openQrDialog"
            data-testid="ct-products-share-qr"
          >
            <v-icon icon="qr_code_2" />
          </v-btn>
        </template>
        <span>Share this product</span>
      </v-tooltip>
      <v-btn
        variant="text"
        class="pcard-action-btn"
        prepend-icon="edit"
        @click.stop="editProduct"
        :disabled="$props.campaign.complete"
        data-testid="ct-products-edit"
      >
        Edit
      </v-btn>
      <!--
        #2259: the label is shortened to "Hide" / "Show" so it stays legible
        in one-third of the card (the full "Hide from storefront" / "Show on
        storefront" phrase clipped and overlapped its icon). The full phrase
        moves into a tooltip on hover.
      -->
      <v-btn
        variant="text"
        class="pcard-action-btn"
        :prepend-icon="hideFromStorefront ? 'visibility_off' : 'visibility'"
        @click.stop="toggleProductHideFromStorefront"
        :disabled="$props.campaign.complete"
        data-testid="ct-products-toggle-visibility"
      >
        {{ hideFromStorefront ? "Show" : "Hide" }}
        <v-tooltip activator="parent" location="top">
          {{ hideFromStorefront ? "Show on storefront" : "Hide from storefront" }}
        </v-tooltip>
      </v-btn>
      <v-btn
        variant="text"
        color="error"
        class="pcard-action-btn"
        prepend-icon="delete"
        @click.stop="deleteProduct"
        :disabled="$props.campaign.complete || $props.campaignProduct.is_ordered"
        data-testid="ct-products-delete"
      >
        <span v-if="$props.campaignProduct.is_ordered" class="pcard-action-hint">
          Delete
          <v-tooltip activator="parent" location="top">
            Cannot delete product after it has been ordered
          </v-tooltip>
        </span>
        <span v-else-if="campaign.complete" class="pcard-action-hint">
          Delete
          <v-tooltip activator="parent" location="top">
            Cannot delete product after campaign is complete
          </v-tooltip>
        </span>
        <span v-else>Delete</span>
      </v-btn>
    </div>

    <!-- Boulders: Storefront color deep-link + QR share code (pebble 2 of
         2), https://trello.com/c/PWUeRl0u. Colors come from the same
         de-duped, ordered sku list the swatch row already computes
         (allSwatches) so the picker offers exactly the colors the card
         itself shows. -->
    <product-qr-share-dialog
      v-model:open="qrDialogOpen"
      :campaign-product-id="campaignProduct.id"
      :product-name="campaignProduct.product_name"
      :store-name="$props.campaign.name"
      :colors="allSwatches.map((s) => s.sku)"
    />
  </v-card>
</template>

<script>
import helpers from "@/mixins/helpers";
import ProductReadinessFlag from "../readiness/ProductReadinessFlag.vue";
import ProductQrShareDialog from "./ProductQrShareDialog.vue";
import PricingModeBadge from "./PricingModeBadge.vue";
import UnpricedSizesMarker from "./UnpricedSizesMarker.vue";
import UncoveredColorsMarker from "./UncoveredColorsMarker.vue";
import InvalidPlacementMarker from "./InvalidPlacementMarker.vue";
import DpiStatusIcon from "@/components/decoration/DpiStatusIcon.vue";
import { fromPriceLabel, unpricedFixedSizes } from "./pricingMode";
import { supplierLabel } from "./supplierLabel";
import { CURRENCY_EMPTY_PLACEHOLDER } from "@/utils/currency";
import {
  chosenInDisplayOrder,
  hasGlobalOrder,
  resolveColorImageRefs,
} from "@/utils/storefrontImages";
import { fallbackHex } from "@/constants/colorHexFallback";

import { mapGetters } from "vuex";

// #2246: neutral grey used for a swatch whose color has no resolvable hex
// (absent from both the backend selected_color_hex map and the frontend
// fallbackHex map). We render a filled grey circle + the name rather than a
// blank/broken swatch. Mirrors the ColorFilmstrip neutral treatment.
const NEUTRAL_SWATCH_HEX = "#9ca3af";

// #2246: how many swatches the card shows before collapsing the rest into a
// "+N more" chip. Keeps a many-color SKU from overflowing the card; the row
// also wraps, so this is a soft cap on visual density, not correctness.
const MAX_VISIBLE_SWATCHES = 8;

export default {
  components: {
    "pricing-mode-badge": PricingModeBadge,
    "unpriced-sizes-marker": UnpricedSizesMarker,
    "uncovered-colors-marker": UncoveredColorsMarker,
    "invalid-placement-marker": InvalidPlacementMarker,
    "dpi-status-icon": DpiStatusIcon,
    "product-readiness-flag": ProductReadinessFlag,
    "product-qr-share-dialog": ProductQrShareDialog,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },

    campaignProduct: {
      type: Object,
      required: true,
    },

    productIncomplete: {
      type: Boolean,
      required: false,
    },

    // Phase 2.2: per-product readiness payload from
    // GET /api/stores/<id>/products_readiness/. Shape:
    //   { campaign_product_id, name, state, count, decoration_count }
    // When null/undefined we render no flag (legacy-safe).
    readiness: {
      type: Object,
      required: false,
      default: null,
    },
  },

  // Explicit emits contract (security reviewer P2, 2026-06-16):
  //   - delete-product: bubble up to ProductEditHost.promptDeleteProduct
  //   - stockLoaded: bubble smart-product stock data up to the dialog
  //   - hide-from-storefront-changed: parent owns the jsonapi-vuex
  //     mutation; we emit { campaignProductId, hidden } after the POST
  //     succeeds. Replaces the prop-mutation anti-pattern.
  emits: [
    "delete-product",
    "stockLoaded",
    "hide-from-storefront-changed",
    "readiness-click",
  ],

  mixins: [helpers],

  data() {
    return {
      // PR-Z2: ``decorations`` data field retired -- the only seeder
      // (``initComponent`` reading ``campaignProduct.decorations``) and
      // the only watcher (on the same prop) were removed when the
      // legacy ``cp.decorations`` JSONList relationship was dropped by
      // P6 PR-2.  No template binding references it; safe to delete.
      selectedValues: {},

      primaryAttributeName: null,
      possiblePrimaryValues: [],
      primaryAttributeFieldName: null,

      secondaryAttributeName: null,
      possibleSecondaryValues: [],
      secondaryAttributeFieldName: null,

      productImages: [],
      // The colors that actually own a carousel PRIMARY slot, in
      // possiblePrimaryValues order, filtered to those with a resolvable
      // image. This is the carousel's index space -- decoupled from
      // possiblePrimaryValues (which lists every selected color for the
      // variant <select>). ``selectedImageId`` and ``changeSlider`` index
      // into this list, not possiblePrimaryValues. Empty only in the
      // zero-images edge case, where productImages holds a single
      // getImage(null) placeholder that carouselColors does not track (so
      // sliding on the lone placeholder is a no-op, not an index error).
      carouselColors: [],
      selectedImageId: 0,
      // True when the carousel's only slide is the product's DERIVED
      // listing image (see ``fallbackImage``). Marks the wrapper with a
      // testid so a scenario can tell it from the placeholder.
      showsDerivedImage: false,

      primaryVisibility: false,
      secondaryVisibility: false,

      loadingStock: false,
      stockMessage: null,
      stockInfo: null,
      isLowStock: false,

      // Boulders: Storefront color deep-link + QR share code (pebble 2 of
      // 2), https://trello.com/c/PWUeRl0u.
      qrDialogOpen: false,

    };
  },

  mounted() {
    this.initComponent();
  },

  computed: {
    // The server's quote, or null (rendered as a neutral placeholder).
    // Fixed-price sizes with no price (#3884 gate P2-2), from the row's
    // server quote: in fixed mode `price_preview` holds exactly the priced
    // sizes.
    unpricedSizes() {
      const cp = this.$props.campaignProduct;
      return unpricedFixedSizes(cp.pricing_mode, cp.selected_sizes, cp.price_preview);
    },
    priceLabel() {
      return fromPriceLabel(this.$props.campaignProduct.price_preview);
    },

    pricePlaceholder() {
      return CURRENCY_EMPTY_PLACEHOLDER;
    },

    ...mapGetters(["getDistributorId"]),
    /**
     * Eye / visibility toggle source-of-truth.
     *
     * Derived from the prop so the icon stays in lockstep with server
     * state. The handler emits `hide-from-storefront-changed` after the
     * POST succeeds; the parent (StoreProducts/index.vue) owns the
     * write into the jsonapi-vuex proxy. A previous implementation
     * mutated the prop in place (Vue anti-pattern, security reviewer
     * P2 2026-06-16); before that, an even older version used a local
     * `data` field + a forced `:key` remount on the v-btn, which made
     * the second click drop on the floor in production.
     */
    hideFromStorefront() {
      return Boolean(this.campaignProduct.hide_from_storefront);
    },
    productIncompleteText() {
      if (
        this.$store.getters.getSmartShippingEnabled &&
        (!this.campaignProduct.product_weight ||
          this.campaignProduct.product_weight == 0)
      ) {
        return "Product weight info empty";
      } else {
        return "Product contains incomplete information";
      }
    },

    /**
     * #2246: the base style number shown in grey under the name. This is the
     * product_item_number (the SanMar STYLE#, e.g. "LST350"), NOT a variant
     * id -- the card describes the product, not a single selected variant.
     */
    baseSku() {
      const n = this.campaignProduct.product_item_number;
      return typeof n === "string" && n.length > 0 ? n : null;
    },

    // Supplier display name, or null for the placeholder. See supplierLabel.js.
    supplierName() {
      return supplierLabel(this.campaignProduct);
    },

    /**
     * #2246: the full display-only swatch list, one entry per selected color
     * (in the store's chosen order), each resolved to a hex:
     *   1. backend ``selected_color_hex[sku]`` (authoritative vendor_color_hex)
     *   2. frontend ``fallbackHex(sku)`` (generic name -> hex map)
     *   3. NEUTRAL_SWATCH_HEX (grey) -- never a blank/broken swatch.
     * ``isFallback`` marks entries whose hex did NOT come from the
     * authoritative backend map, so the UI can flag them as approximate.
     */
    allSwatches() {
      const cp = this.$props.campaignProduct;
      const skus = Array.isArray(cp.selected_color_skus)
        ? cp.selected_color_skus
        : [];
      const backendHex =
        cp.selected_color_hex && typeof cp.selected_color_hex === "object"
          ? cp.selected_color_hex
          : {};
      // De-dup while preserving order -- a partial-save race can repeat a sku
      // (same guard ColorFilmstrip applies) which would collide the v-for key.
      const seen = new Set();
      const out = [];
      for (const sku of skus) {
        if (typeof sku !== "string" || sku.length === 0 || seen.has(sku)) {
          continue;
        }
        seen.add(sku);
        const authoritative = backendHex[sku] || null;
        const hex = authoritative || fallbackHex(sku) || NEUTRAL_SWATCH_HEX;
        out.push({
          sku,
          hex,
          isFallback: !authoritative,
        });
      }
      return out;
    },

    /**
     * #2246: the swatches actually rendered, capped at MAX_VISIBLE_SWATCHES so
     * a many-color SKU does not overflow. The remainder collapses into a
     * "+N more" chip (see hiddenSwatchCount). When the cap is not exceeded,
     * every swatch is shown.
     */
    visibleSwatches() {
      return this.allSwatches.slice(0, MAX_VISIBLE_SWATCHES);
    },

    /**
     * #2246: how many selected colors are hidden behind the "+N more" chip.
     * Zero when the swatch count is within the cap.
     */
    hiddenSwatchCount() {
      return Math.max(0, this.allSwatches.length - MAX_VISIBLE_SWATCHES);
    },
  },

  watch: {
    // PR-Z2 (Q0PhzeSn / cQ2XCe07): the
    // ``campaignProduct.decorations`` watcher was retired with the
    // legacy ``cp.decorations`` relationship (dropped by P6 PR-2 -- the
    // field now resolves to ``undefined`` and would cause the watcher
    // to fire spuriously on every CP load).  Decoration data lives on
    // ``CampaignProduct.placements`` (new substrate) and is consumed
    // by the placement-rooted editor surfaces, not by this card.

    "campaignProduct.attributes": function(newVal, oldVal) {
      this.initComponent();
    },
  },

  methods: {
    onReadinessClick(payload) {
      // Bubble the click to the parent (StoreProducts/index.vue),
      // which owns the Approval Workspace navigation. Keeping the
      // routing in the parent makes ProductCard mount-testable
      // without a router stub.
      this.$emit("readiness-click", payload);
    },

    async initComponent() {
      // PR-Z2: the seeding of ``this.decorations`` from
      // ``campaignProduct.decorations`` (which is now ``undefined``
      // post P6 PR-2) was retired with the legacy decoration walk;
      // the local ``decorations`` data field was deleted with it.
      this.primaryAttributeFieldName = this.campaignProduct.primary_attribute_name;
      this.secondaryAttributeFieldName = this.campaignProduct.secondary_attribute_name;

      // The card's color list is the UNION of the legacy attribute keys,
      // the Storefront image-picker's storefront_images keys, and the
      // picker's selected_color_skus. Follow-on to #2005: #2005 wired
      // resolveColorImageRefs into buildProductImages but left this list
      // gated on legacy ``attributes`` alone. The new picker populates
      // storefront_images + selected_color_skus and leaves attributes {},
      // so a picker-only product produced an empty color list -> zero
      // colors iterated -> a blank card even though an image was picked
      // (Jake, 8083 store 13 product 54).
      //
      // Legacy keys come first (a pure-legacy product's list is byte-for
      // -byte unchanged -- the union collapses to Object.keys(attributes)),
      // then any picker/selected colors not already present in their own
      // order. possiblePrimaryValues is the single index space shared by
      // the carousel (selectedImageId), the primary <select>, and
      // buildProductImages, so the color-index invariant holds across all
      // three.
      //
      // Bug fix 2026-06-19: campaignProduct.attributes can arrive null when
      // a campaign-product row is mid-load (jsonapi-vuex returns the
      // relationship as null until the included payload merges) or when a
      // recently-created CP has not had its attribute graph populated yet.
      // Object.keys(null) throws a hard TypeError; the ``|| {}`` /
      // ``|| []`` defaults keep every source safe.
      this.possiblePrimaryValues = this.buildCardColorList();
      // check if the attribute has only None
      this.primaryVisibility = this.checkPrimaryAttributeVisibility(
        this.possiblePrimaryValues
      );

      // Pick the primary color from the FULL selected-color list: prefer
      // default_image, else the first color. selectedImageId (the carousel
      // index) is derived from carouselColors AFTER buildProductImages runs,
      // since the carousel is filtered to image-bearing colors and no longer
      // aligns 1:1 with possiblePrimaryValues.
      let primaryColorIndex = this.possiblePrimaryValues.indexOf(
        this.$props.campaignProduct.default_image
      );
      // GLOBAL order (Jake 2026-10-07): with no default color, the card opens
      // on the FIRST chosen image -- the head of the distributor's sort --
      // rather than whichever color happens to list first.
      const storefrontImages = this.$props.campaignProduct.storefront_images;
      if (primaryColorIndex < 0 && hasGlobalOrder(storefrontImages)) {
        const [first] = chosenInDisplayOrder(storefrontImages, this.possiblePrimaryValues);
        if (first) primaryColorIndex = this.possiblePrimaryValues.indexOf(first.color);
      }

      if (this.possiblePrimaryValues.length > 0) {
        // Fall back to the first available primary value when default_image
        // is not in the seeded subset (issue #859). Without this guard,
        // possiblePrimaryValues[-1] is undefined and the spread below
        // throws "is not iterable".
        if (primaryColorIndex < 0) {
          primaryColorIndex = 0;
        }
        this.primaryAttributeName = this.possiblePrimaryValues[
          primaryColorIndex
        ];
      }
      this.selectedValues[
        this.primaryAttributeFieldName
      ] = this.primaryAttributeName;

      const possibleSecondaryValues = [];
      // primaryAttributeName may now be a picker-only color absent from the
      // legacy attributes map (or attributes may be null); ``|| {}`` keeps
      // the lookup safe and returns undefined -> the Array.isArray guard
      // below leaves possibleSecondaryValues empty (no sizes for that color).
      const secondaryForPrimary = this.primaryAttributeName
        ? (this.$props.campaignProduct.attributes || {})[this.primaryAttributeName]
        : null;
      if (Array.isArray(secondaryForPrimary)) {
        possibleSecondaryValues.push(...secondaryForPrimary);
      }
      this.possibleSecondaryValues = possibleSecondaryValues;
      // check if the attribute has only None
      this.secondaryVisibility = this.checkSecondaryAttributeVisibility(
        this.possibleSecondaryValues
      );

      if (this.possibleSecondaryValues.length > 0) {
        this.secondaryAttributeName = this.possibleSecondaryValues[0];
      }

      this.selectedValues[
        this.secondaryAttributeFieldName
      ] = this.secondaryAttributeName;

      // if no variant is selected, this will display the default image.
      // Populates this.carouselColors (image-bearing colors, carousel order).
      this.productImages = this.buildProductImages();

      // selectedImageId indexes the carousel (carouselColors), NOT
      // possiblePrimaryValues. Point it at the chosen primary color's slot;
      // if that color has no image (absent from carouselColors) or nothing
      // has an image at all, fall back to slot 0 (the first real image, or
      // the lone placeholder in the zero-images edge).
      const carouselIndex = this.carouselColors.indexOf(this.primaryAttributeName);
      this.selectedImageId = carouselIndex < 0 ? 0 : carouselIndex;
    },

    // Build the card's ordered, de-duplicated color list -- the single
    // index space shared by the carousel, the primary <select>, and
    // buildProductImages. Union of three sources, legacy first so a
    // pure-legacy product is unchanged:
    //   1. legacy attribute keys (Object.keys(attributes))
    //   2. Storefront image-picker keys (Object.keys(storefront_images))
    //   3. picker selected_color_skus (array of chosen colors)
    // Sources 2 and 3 exist only for products that used the new picker;
    // for legacy products they are absent/empty and the union collapses
    // to the legacy attribute keys exactly as before.
    buildCardColorList() {
      const cp = this.$props.campaignProduct;
      const seen = new Set();
      const colors = [];
      const add = (color) => {
        if (typeof color === "string" && color.length > 0 && !seen.has(color)) {
          seen.add(color);
          colors.push(color);
        }
      };
      Object.keys(cp.attributes || {}).forEach(add);
      Object.keys(cp.storefront_images || {}).forEach(add);
      (Array.isArray(cp.selected_color_skus) ? cp.selected_color_skus : []).forEach(
        add
      );
      return colors;
    },

    // Build the per-color image list the carousel renders, AND the parallel
    // ``carouselColors`` list naming which color owns each primary slot.
    //
    // The carousel shows ONLY colors that HAVE an image: for each color (in
    // ``possiblePrimaryValues`` order) we resolve its primary image PREFERRING
    // the builder's per-color ``storefront_images`` selection (the documented
    // single source of truth -- ordered list, first entry = primary) and
    // FALLING BACK to the legacy ``images[color]`` single image when the
    // picker was never used. ``resolveColorImageRefs`` is the SAME shared
    // resolver the shopper card / quickview / cart use, so every surface
    // agrees from one place. A color with NO image from either source
    // contributes NO carousel slot -- previously each image-less selected
    // color pushed a getImage(null) placeholder, so a product with 3 selected
    // colors but 1 picked image showed 1 real image + 2 default-product.svg
    // placeholders (Jake, 8083 store 6 PC54). ``possiblePrimaryValues`` stays
    // the FULL selected-color list for the variant <select>; only the carousel
    // is filtered to image-bearing colors.
    //
    // Because image-less colors are dropped, the carousel index no longer
    // aligns 1:1 with possiblePrimaryValues -- ``carouselColors`` records the
    // color for each primary slot so ``changeSlider`` and ``selectedImageId``
    // resolve the correct color by identity (see changeSlider / initComponent).
    //
    // ``getImage`` passes an already-resolved absolute URL through unchanged
    // and prefixes a bare uuid with the S3 base, so one call site handles both
    // a ``storefront_images`` URL and a legacy uuid.
    //
    // Extra picker images for a color append after all primaries as a gallery
    // tail, mirroring StorefrontProductCard.buildProductImages.
    //
    // ZERO-images edge: when NO selected color has any image, primaries is
    // empty. We push a SINGLE getImage(null) placeholder so the carousel is
    // never blank; carouselColors stays empty (the placeholder owns no color),
    // so sliding on the lone placeholder is a no-op rather than an index bug.
    buildProductImages() {
      if (hasGlobalOrder(this.$props.campaignProduct.storefront_images)) {
        return this.buildGlobalOrderImages();
      }
      const primaries = [];
      const galleryTail = [];
      const carouselColors = [];
      this.possiblePrimaryValues.forEach((element) => {
        const refs = resolveColorImageRefs(this.$props.campaignProduct, element);
        if (refs.length > 0) {
          carouselColors.push(element);
          primaries.push(this.getImage(refs[0]));
          refs.slice(1).forEach((ref) => {
            galleryTail.push(this.getImage(ref));
          });
        }
      });
      this.carouselColors = carouselColors;
      if (primaries.length === 0) {
        // No color has an image -- one fallback slide (the derived listing
        // photo, else the placeholder) so the carousel is never blank.
        return [this.fallbackImage()];
      }
      this.showsDerivedImage = false;
      return [...primaries, ...galleryTail];
    },

    // GLOBAL order (Jake 2026-10-07, card https://trello.com/c/Qlwx8mlT):
    // once the Storefront listing has given the chosen images a ``position``,
    // the carousel shows them in that order, across colors -- exactly the
    // Chosen grid's order (chosenInDisplayOrder is shared with it). Here
    // ``carouselColors`` names the color of EVERY slide, so a color can own
    // several slides; a swatch or the default color opens on its first.
    // A color with no chosen image keeps its legacy image (the same
    // resolveColorImageRefs fallback as above), after the chosen ones.
    buildGlobalOrderImages() {
      const cp = this.$props.campaignProduct;
      const slides = [];
      const slideColors = [];
      for (const { color, entry } of chosenInDisplayOrder(
        cp.storefront_images,
        this.possiblePrimaryValues
      )) {
        slides.push(this.getImage(entry.url));
        slideColors.push(color);
      }
      const chosen = new Set(slideColors);
      this.possiblePrimaryValues.forEach((color) => {
        if (chosen.has(color)) return;
        resolveColorImageRefs(cp, color).forEach((ref) => {
          slides.push(this.getImage(ref));
          slideColors.push(color);
        });
      });
      this.carouselColors = slideColors;
      if (slides.length === 0) return [this.fallbackImage()];
      this.showsDerivedImage = false;
      return slides;
    },

    // The one slide a card with NO chosen and no legacy image shows. Bug
    // Intake #460 (Jake 2026-10-23): the list endpoint DERIVES the default
    // color's render or supplier photo at read time and sends it as
    // ``derived_listing_image`` (null when it cannot); it is never written
    // to Chosen, so a color or SKU swap changes it on the next list load.
    // Without one, the default-product placeholder, as before.
    fallbackImage() {
      const derived = this.$props.campaignProduct.derived_listing_image;
      this.showsDerivedImage = Boolean(derived);
      return this.getImage(derived || null);
    },

    async getStock() {
      // load smart product stock info
      if (this.campaignProduct.is_smart_product) {
        this.loadingStock = true;
        let result;
        try {
          result = await this.$axios.post("/campaign_product/getStock", {
            sku: this.campaignProduct.product_item_number,
            supplier_id: this.campaignProduct.supplier.id,
            attributes: this.campaignProduct.attributes,
            product_id: this.campaignProduct.id,
          });
        } catch (err) {
          this.$store.dispatch('raiseError', err);
          this.stockMessage = err;
          this.$emit("stockLoaded", this.campaignProduct, []);
        } finally {
          this.isLowStock = false;
          this.stockInfo = result.data.error ? [] : result.data;

          for (let i = 0; i < this.stockInfo.length; i++) {
            const element = this.stockInfo[i];

            for (const val of Object.values(element.low_stock_alert)) {
              if (val) {
                this.isLowStock = true;
                break; // Break out of the inner loop
              }
            }

            if (this.isLowStock) {
              break; // Break out of the outer loop
            }
          }
          this.loadingStock = false;
          this.$emit("stockLoaded", this.campaignProduct, this.stockInfo);
          // this.$refs["stock-availability-dialog"].show();
        }
      }
    },

    async toggleProductHideFromStorefront() {
      // The icon is derived from the prop (`hideFromStorefront`
      // computed). Round-trip through the backend first; on success,
      // emit `hide-from-storefront-changed` so the parent
      // (StoreProducts/index.vue) writes the new value into the
      // jsonapi-vuex proxy. The backend toggle is `not
      // hide_from_storefront` (not "set to X"), so a `!current` mirror
      // keeps the client and server in agreement without needing the
      // response payload to carry the new value.
      //
      // Security reviewer P2 (2026-06-16): the previous impl mutated
      // `this.campaignProduct.hide_from_storefront` in place. That is
      // the prop-mutation anti-pattern - the parent owns the data, the
      // child emits up. A caller passing a frozen / readonly proxy
      // would have crashed here.
      const next = !this.hideFromStorefront;
      try {
        await this.$axios.post(
          `/campaign_product/toggleHideFromStorefront`,
          {
            campaign_product_id: this.campaignProduct.id,
          }
        );
        this.$emit("hide-from-storefront-changed", {
          campaignProductId: this.campaignProduct.id,
          hidden: next,
        });
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      }
    },

    async checkStock() {
      this.$refs["stock-availability-dialog"].show();
    },

    checkPrimaryAttributeVisibility(attributes) {
      if (attributes && attributes.length == 1 && attributes[0] === "N/A") {
        return false;
      }
      return true;
    },

    checkSecondaryAttributeVisibility(attributes) {
      if (attributes && attributes.length == 1 && attributes[0] === "N/A") {
        return false;
      }
      return true;
    },

    updateSelectedVariant(attrName, attrValue) {
      this.selectedValues[attrName] = attrValue;
      this.primaryAttributeName = attrValue;
      const possibleSecondaryValues = [];

      // possiblePrimaryValues is now the UNION of legacy attribute keys and
      // picker colors (buildCardColorList), so the selected color may have
      // no legacy ``attributes[color]`` entry (a picker-only color). Guard
      // the spread -- an unguarded attributes[undefined-color] threw
      // "... is not iterable" and would surface as a console error when the
      // slider landed on a picker-only color.
      const secondaryForPrimary =
        (this.$props.campaignProduct.attributes || {})[this.primaryAttributeName];
      if (Array.isArray(secondaryForPrimary)) {
        possibleSecondaryValues.push(...secondaryForPrimary);
      }
      this.possibleSecondaryValues = possibleSecondaryValues;
      // check if the attribute has only None
      this.secondaryVisibility = this.checkSecondaryAttributeVisibility(
        this.possibleSecondaryValues
      );

      if (this.possibleSecondaryValues.length > 0) {
        this.secondaryAttributeName = this.possibleSecondaryValues[0];
        this.selectedValues[
          this.secondaryAttributeFieldName
        ] = this.secondaryAttributeName;
      }

      // selectedImageId indexes the carousel (carouselColors), not the full
      // possiblePrimaryValues list. When the selected color has a carousel
      // slot, point the carousel at it; when it does not (an image-less color
      // chosen via the <select>), leave the carousel where it is rather than
      // driving it to -1 (which would break the v-carousel v-model).
      // Already on a slide of this color (a color can own several slides in
      // the global order): stay there rather than snapping to its first.
      if (this.carouselColors[this.selectedImageId] === this.primaryAttributeName) {
        return;
      }
      const carouselIndex = this.carouselColors.findIndex(
        (color) => color === this.primaryAttributeName
      );
      if (carouselIndex >= 0) {
        this.selectedImageId = carouselIndex;
      }
    },

    updateSelectedVariantBySize(attrName, attrValue) {
      this.selectedValues[attrName] = attrValue;
      this.secondaryAttributeName = attrValue;
    },

    changeSlider(event) {
      // ``event`` is the carousel slot index, which maps to a color via
      // carouselColors (image-bearing colors only), NOT possiblePrimaryValues
      // (the full selected-color list). Using possiblePrimaryValues[event]
      // here would select the wrong color once image-less colors are dropped
      // from the carousel. In the zero-images edge carouselColors is empty and
      // the lone placeholder owns no color, so guard the lookup.
      const color = this.carouselColors[event];
      if (color === undefined) {
        return;
      }
      this.updateSelectedVariant(this.primaryAttributeFieldName, color);
    },

    // Store-editor Products page polish (Jake direct ask, no card --
    // polish/UX carve-out): clicking a color swatch swaps THIS card's
    // carousel preview to that color's image. Transient only -- purely
    // local component state (selectedImageId / primaryAttributeName via
    // updateSelectedVariant), no persistence, no URL change, no saved
    // data; resets to the default on reload since nothing here writes to
    // campaignProduct or the server.
    //
    // Reuses carouselColors -- the SAME carousel index space changeSlider
    // already resolves against -- rather than inventing a parallel
    // mapping (see the carouselColors doc comment in data()). A color
    // with NO carousel slot (no resolvable image) is a no-op: we do not
    // move the slide, throw, or blank the frame.
    selectSwatch(sku) {
      const carouselIndex = this.carouselColors.indexOf(sku);
      if (carouselIndex < 0) {
        return;
      }
      this.selectedImageId = carouselIndex;
      this.updateSelectedVariant(this.primaryAttributeFieldName, sku);
    },

    // Store-editor Products page polish: the carousel's prev/next arrows
    // are rendered internally by Vuetify (VWindow) and are not markup we
    // own, so we can't attach @click.stop to them directly. This wrapper
    // handler inspects the clicked element and stops propagation ONLY
    // when it (or an ancestor within the wrapper) is an arrow button --
    // identified by the SAME .v-window__left / .v-window__right classes
    // targeted by the de-circle style rule below (verified against the
    // installed Vuetify 3 source: VWindow puts these classes directly on
    // the button root). A click on the image itself is left alone so it
    // falls through to the card-level navigate, per Jake's "image opens
    // the editor" requirement.
    onCarouselWrapperClick(event) {
      const arrow = event.target.closest(".v-window__left, .v-window__right");
      if (arrow) {
        event.stopPropagation();
      }
    },

    // Day 6.5 canonical wiring: the pencil now routes directly into the
    // canonical product editor (formerly the polish-sprint demo shell,
    // graduated to /distributor/product-editor/:id). The prior
    // editProduct chain (fetch + smart-product hydration + emit to the
    // legacy ProductCreationModal) was retired along with the legacy
    // ProductEditor.vue + components/ + sections/DecorationsHotspot; the
    // modal itself was deleted in the 2026-09-01 dead-code sweep.
    // The wand affordance (auto_fix_high / openNewEditor) is gone; the
    // pencil is the only edit affordance.
    editProduct() {
      this.$router.push({
        name: "product_editor",
        params: { campaignProductId: String(this.$props.campaignProduct.id) },
      });
    },

    deleteProduct() {
      this.$emit("delete-product", this.$props.campaignProduct);
    },

    // Boulders: Storefront color deep-link + QR share code (pebble 2 of
    // 2), https://trello.com/c/PWUeRl0u. The dialog fetches its own QR +
    // URL on open (see ProductQrShareDialog's `open` watcher).
    openQrDialog() {
      this.qrDialogOpen = true;
    },
  },
};
</script>

<style scoped>
.pcard {
  position: relative;
  min-height: 200px;
}

.pcard .title {
  min-height: 50px;
}

.pcard div {
  position: relative;
}

.pcard :deep(.v-image__image) {
  background-size: contain !important;
}

/* The whole card is a click target that navigates to the product editor
   (see @click="editProduct" on the v-card root). */
.pcard-clickable {
  cursor: pointer;
}

/* Bare-caret carousel arrows, pinned to the card edges.
   ROOT CAUSE PART 1 -- the circle (verified against the installed Vuetify
   3.7.5 source): <v-carousel> does NOT render its own .v-carousel__prev /
   .v-carousel__next wrapper -- it delegates prev/next navigation entirely
   to VWindow, which puts the class DIRECTLY on the VBtn's root element as
   ``v-window__left`` (prev) / ``v-window__right`` (next) for the default
   LTR/non-reversed case. There is no ".v-carousel__prev .v-btn" element in
   the rendered DOM at all, so the PRIOR de-circle rule here (and the
   byte-identical global style.css:204-207 rule) never matched anything --
   that's why the arrows still rendered as grey circles despite that rule's
   presence.

   ROOT CAUSE PART 2 -- the inset (found via live DOM measurement on a
   deployed sandbox after a reviewer caught that ``left: 0 !important`` had
   NO visual effect): the buttons are FLEX CHILDREN of
   ``.v-window__controls`` (VWindow.css: position:absolute; left:0;
   width:100%; display:flex; justify-content:space-between; padding: 0
   16px) -- their own position is ``relative`` (VBtn's default), so a
   ``left``/``right`` offset on the button itself only shifts it from its
   flow position; since both were 0 it was a genuine no-op. The ACTUAL
   ~31px inset per side (measured: card left=584, button left=615) was
   ``.v-window__controls``'s own ``padding: 0 16px`` (16px) STACKED with
   the ``v-card`` root's inline ``padding: 14px`` + ``border: 1px`` (15px)
   insetting ``.v-window`` itself. Fixed by zeroing ``.v-window__controls``
   padding so the buttons sit flush against ``.v-window``'s box -- which is
   the card's PADDING-box edge (14px+1px in from the border), matching the
   card's existing internal margin rather than fighting it. Scoped under
   .pcard via :deep() so it affects ONLY this card's carousel, not any
   other v-window/v-carousel app-wide. */
.pcard :deep(.v-window__controls) {
  padding: 0 !important;
}

.pcard :deep(.v-window__left),
.pcard :deep(.v-window__right) {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  color: var(--tf-fg-4) !important;
}

.lazy-img-fadein[lazy="loaded"] {
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
  -webkit-animation-name: fadeIn;
  animation-name: fadeIn;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

/* #2259: the top-row indicators. The left cluster is offset right so the
   Smart-Product bolt clears the parent grid's top-left selection checkbox
   (top:8px;left:8px, 28px box) instead of sitting under it. */
.pcard-top-left-cluster {
  padding-left: 30px;
}

.product-title {
  display: -webkit-box;
  /* #2259: shrunk from 22px; height tracks the 2-line clamp at the new size
     so there is no empty gap below a single-line name. */
  height: 46px;
  margin-top: 0;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  padding-right: 12px;
  padding-left: 12px;
}

/* #2246: base style number, grey, directly under the name. The supplier
   line (2026-10-05) above it shares the same meta-line styling. */
.pcard-supplier,
.pcard-base-sku {
  padding-left: 12px;
  padding-right: 12px;
  margin-bottom: 2px;
  color: var(--tf-text-muted, #6b7280);
  font-size: 13px;
  font-weight: 400;
}

.pcard-price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* #2259: price shrunk from 30px and tightened under the sku. */
.price {
  padding-left: 12px;
  padding-right: 12px;
  font-size: 19px;
  font-weight: 600;
}

/* #2246: color swatch row. Wraps to multiple lines and is capped by
   MAX_VISIBLE_SWATCHES + a "+N more" chip so a many-color SKU never
   overflows the card. */
.pcard-swatches {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 10px;
  padding: 8px 12px 2px;
}

.pcard-swatch {
  display: flex;
  align-items: center;
  /* #2246 declutter: the name-below-dot label was removed -- the color name
     now lives in a hover tooltip on the dot, so the cell only holds the dot
     and sizes to it (no min/max width for a wrapping label). */
}

/* Store-editor Products page polish: swatches became interactive (click
   swaps the card's carousel preview -- selectSwatch()), so the dot itself
   (which carries the click/keydown handlers) gets a pointer cursor. */
.pcard-swatch-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  flex: 0 0 auto;
  cursor: pointer;
}

/* Approximate-color affordance: the hex did NOT come from the authoritative
   backend map (fallbackHex map or neutral grey). A dashed border signals
   "generated, not exact", matching the ColorFilmstrip treatment. */
.pcard-swatch-dot.is-fallback {
  border-style: dashed;
  border-color: rgba(0, 0, 0, 0.35);
}

.pcard-swatch-more {
  align-self: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--tf-text-muted, #6b7280);
  white-space: nowrap;
}

/* #2246: the three explicit action buttons (Edit / Hide / Delete). */
.pcard-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 8px 0 0;
}

/* #2259: smaller font + icon so the three labels fit legibly across the
   card without clipping or overlapping their icons. */
.pcard-action-btn {
  text-transform: none;
  letter-spacing: 0;
  flex: 1 1 0;
  min-width: 0;
  font-size: 12px;
  padding-inline: 6px;
}

.pcard-action-btn :deep(.v-btn__prepend) {
  margin-inline-end: 4px;
}

.pcard-action-btn :deep(.v-icon) {
  font-size: 18px;
}

/* Boulders: Storefront color deep-link + QR share code (pebble 2 of 2).
   A subtle icon-only button -- does NOT take a flex slot away from the
   three text actions (Edit/Hide/Delete), just sits to their left at its
   natural size, matching the top-row icon buttons (Smart-Product bolt /
   incomplete-flag). */
.pcard-share-btn {
  flex: 0 0 auto;
}

.pcard-share-btn :deep(.v-icon) {
  font-size: 20px;
}

.pcard-action-hint {
  display: inline-flex;
  align-items: center;
}

.add-to-cart-btn {
  position: absolute;
  bottom: 8px;
  width: 100%;
  padding-right: 58px !important;
}

.hidden-product-shade {
  height: 83%;
  width: 100%;
  z-index: 2;
  position: absolute !important;
  background: linear-gradient(to bottom, rgb(0 0 0 / 17%), rgba(0, 0, 0, 0));
  right: 0px;
  top: 0px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}

.hidden-product-shade-text {
  text-transform: uppercase;
  font-weight: 500;
  color: white;
  text-align: center;
  font-size: 25px;
  text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.176);
  position: absolute !important;
  top: 156px;
  user-select: none;
}
</style>

